import React from "react";
import { useTranslation } from "react-i18next";
import { useProductAddToCart } from "../../../../cart/hooks/useProductAddToCart";
import { TransformedProduct } from "../../../types/productApiTypes";
import Icon from "../../../../../../common/components/Icon/Icon";
import { useOpenStockNotificationModal } from "../../../../stockNotification/hooks/useOpenStockNotificationModal";

interface CardActionsProps {
  product: TransformedProduct;
  onQuickViewClick: (productId: number) => void;
  /** Whether to show the Quick View button. Defaults to true. */
  showQuickViewButton?: boolean;
}

/**
 * Renders the action buttons at the bottom of the card.
 */
const CardActions: React.FC<CardActionsProps> = ({
  product,
  onQuickViewClick,
  showQuickViewButton = true, // Default to true for backward compatibility
}) => {
  const { t } = useTranslation();
  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const openStockModal = useOpenStockNotificationModal();

  // For external products, we only show a single button linking to the external URL
  if (product.type === "external") {
    return (
      <div className="product-card__actions">
        <a
          href={product.addToCartUrl}
          className="product-card__button"
          target="_blank"
          rel="noopener noreferrer"
          title={product.addToCartText}
          style={{ width: "100%" }}
        >
          <span>{t("productCard.goToExternalProductLink")}</span>
          <Icon iconName="external-link" className="icon--stroke-thicker" />
        </a>
      </div>
    );
  }

  // If the product is variable but lacks a default variation, or if it's grouped,
  // we prompt the user to select options via Quick View or Single Page.
  // Note: Even if OOS, we send them to details/quickview to see specific variation status.
  if (
    (product.isVariable && !product.hasDefaultVariation) ||
    product.type === "grouped"
  ) {
    return (
      <div className="product-card__actions">
        <button
          className="product-card__button"
          style={{ width: "100%" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onQuickViewClick(product.id);
          }}
          title={t("productCard.selectOptionsTitle")}
        >
          <span>{t("productCard.selectOptions")}</span>
          <Icon
            iconName="adjustments-horizontal-filled"
            className="icon--filled"
          />
        </button>
      </div>
    );
  }

  // --- Default Logic for Simple Products & Variable Products WITH a default variation ---

  // 1. Check Purchasability
  const isPurchasable = product.purchasable;

  // 2. Check Stock
  const isOutOfStock = product.stockStatus === "outofstock";
  const isOnBackorder = product.onBackorder;

  // 3. Combined Disable Logic
  // IMPORTANT: We no longer disable the button if OutOfStock. We change its action to 'Notify Me'.
  // However, we still disable if it's strictly not purchasable (e.g. price 0).
  const isAddToCartDisabled = isAddingToCart || !isPurchasable;

  const handleQuickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    let idToAdd = product.id;
    let variationData: { attribute: string; value: string }[] | undefined;

    // --- ROBUSTNESS UPDATE: Extract attributes from Default Variation ---
    if (
      product.isVariable &&
      product.hasDefaultVariation &&
      product.defaultVariationData
    ) {
      idToAdd = product.defaultVariationData.id;

      // Map the attributes from the RawDefaultVariation structure to the API format
      variationData = product.defaultVariationData.attributes.map((attr) => ({
        attribute: attr.taxonomy, // e.g., 'pa_color'
        value: attr.term.slug, // e.g., 'blue'
      }));
    }

    handleAddToCart(e, {
      productId: idToAdd,
      variation: variationData, // Pass explicit attributes
    });
  };

  const handleNotifyMe = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Determine IDs.
    // If it's a variable product with a default, use that default variation's ID.
    // Otherwise use the product ID (simple product).
    const targetProductId = product.id;
    const targetVariationId =
      product.isVariable &&
      product.hasDefaultVariation &&
      product.defaultVariationData
        ? product.defaultVariationData.id
        : undefined;

    openStockModal({
      productId: targetProductId,
      variationId: targetVariationId,
      productName: product.name,
    });
  };

  return (
    <div className="product-card__actions">
      <div className="product-card__button-group">
        {/* Conditionally render Quick View button based on prop */}
        {showQuickViewButton && (
          <button
            className="product-card__button"
            title={t("productCard.viewProductTitle")}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickViewClick(product.id);
            }}
          >
            <span>{t("productCard.viewProduct")}</span>
            <Icon iconName="eye-filled" className="icon--filled" />
          </button>
        )}

        {/* --- DYNAMIC ACTION BUTTON --- */}
        {/* Logic: 
            If Out of Stock (and not Backorder) -> Show "Notify Me"
            Else -> Show "Add to Cart" 
        */}
        {isOutOfStock && !isOnBackorder ? (
          <button
            className="product-card__button product-card__button--notify"
            title={t("productCard.notifyMeTitle", "Subscribe to alerts")}
            onClick={handleNotifyMe}
          >
            <span>{t("productCard.notifyMe", "Notify Me")}</span>
            <Icon iconName="bell" className="icon--stroke-thickest" />
          </button>
        ) : (
          <button
            className="product-card__button product-card__button--add-to-cart"
            title={t("productCard.addToCartTitle")}
            onClick={handleQuickAdd}
            disabled={isAddToCartDisabled}
          >
            {isAddingToCart ? (
              <span>{t("productCard.addingToCart")}</span>
            ) : !isPurchasable ? (
              // State 1: Not Purchasable
              <span>{t("productCard.unavailable", "Unavailable")}</span>
            ) : isOnBackorder ? (
              // State 3: Backorder (Enabled)
              <>
                <span>{t("productCard.backorder", "Backorder")}</span>
                <Icon
                  iconName="shopping-cart-filled"
                  className="icon--filled"
                />
              </>
            ) : (
              // State 4: Standard
              <>
                <span>{t("productCard.addToCart")}</span>
                <Icon
                  iconName="shopping-cart-filled"
                  className="icon--filled"
                />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CardActions;
